import React, { useState } from 'react';
import {
  Box, Grid, Paper, Typography, TextField, MenuItem, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress,
} from '@mui/material';
import { Assessment as ReportIcon, FileDownload as DownloadIcon, Print as PrintIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { accountingAPI } from '../../services/api';
import { money, label, errorText } from '../../utils/format';
import { fmtDate, localDate, monthStart } from '../../utils/dates';
import { printPage, esc } from '../../utils/print';
import { MiniStat } from './common';

function CategoryTable({ title, rows, color }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', height: '100%' }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ p: 2 }}>{title}</Typography>
      <Table size="small">
        <TableBody>
          {(rows || []).map((r) => (
            <TableRow key={r.category}><TableCell>{label(r.category)}</TableCell><TableCell align="right" sx={{ fontWeight: 700, color }}>{money(r.total)}</TableCell></TableRow>
          ))}
          {(!rows || rows.length === 0) && <TableRow><TableCell colSpan={2} align="center" sx={{ color: 'text.secondary', py: 2 }}>None</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Paper>
  );
}

export default function ReportsTab({ notify }) {
  const [type, setType] = useState('period');
  const [date, setDate] = useState(localDate());
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(localDate());
  const [year, setYear] = useState(new Date().getFullYear());
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      let r;
      if (type === 'daily') r = await accountingAPI.getDailyReport(date);
      else if (type === 'yearly') r = await accountingAPI.getYearlyReport(year);
      else r = await accountingAPI.getMonthlyReport(from, to);
      setResult({ type, ...r.data });
    } catch (e) { notify(errorText(e, 'Could not generate the report'), 'error'); }
    setLoading(false);
  };

  const title = !result ? '' : result.type === 'yearly' ? `Financial year report ${result.year}` : result.type === 'daily' ? `Daily report - ${fmtDate(result.startDate)}` : `Report ${fmtDate(result.startDate)} to ${fmtDate(result.endDate)}`;

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([{ 'Total income (₹)': Number(result.totalIncome), 'Total outflow (₹)': Number(result.totalExpense), 'Net (₹)': Number(result.netBalance) }]), 'Summary');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet((result.incomeByCategory || []).map((c) => ({ Category: c.category, 'Amount (₹)': Number(c.total) }))), 'Income by category');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet((result.outflowByCategory || []).map((c) => ({ Category: c.category, 'Amount (₹)': Number(c.total) }))), 'Outflow by category');
    if (result.entries) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(result.entries.map((e) => ({
        Date: e.date, Type: e.type, Reference: e.ref || '', Party: e.party || '', Category: e.category, Details: e.description || '', 'Received (₹)': Number(e.in), 'Paid (₹)': Number(e.out),
      }))), 'Entries');
    }
    if (result.months) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(result.months.map((m) => ({ Month: m.month, 'Income (₹)': Number(m.income), 'Outflow (₹)': Number(m.outflow), 'Net (₹)': Number(m.net) }))), 'Months');
    }
    XLSX.writeFile(wb, `accounts_${result.type}_${localDate()}.xlsx`);
  };

  const print = () => {
    const cat = (rows) => (rows || []).map((r) => `<tr><td>${esc(label(r.category))}</td><td class="r b">${money(r.total)}</td></tr>`).join('') || '<tr><td colspan="2">None</td></tr>';
    const body = `<div class="grid">
        <div class="card"><div class="v green">${money(result.totalIncome)}</div><div class="l">Total income</div></div>
        <div class="card"><div class="v orange">${money(result.totalExpense)}</div><div class="l">Total outflow</div></div>
        <div class="card"><div class="v">${money(result.netBalance)}</div><div class="l">Net</div></div></div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px"><div><h3>Income by category</h3><table><tbody>${cat(result.incomeByCategory)}</tbody></table></div>
      <div><h3>Outflow by category</h3><table><tbody>${cat(result.outflowByCategory)}</tbody></table></div></div>
      ${result.months ? `<h3>Month by month</h3><table><thead><tr><th>Month</th><th class="r">Income</th><th class="r">Outflow</th><th class="r">Net</th></tr></thead><tbody>${result.months.map((m) => `<tr><td>${m.month}</td><td class="r">${money(m.income)}</td><td class="r">${money(m.outflow)}</td><td class="r b">${money(m.net)}</td></tr>`).join('')}</tbody></table>` : ''}
      ${result.entries ? `<h3>All entries</h3><table><thead><tr><th>Date</th><th>Type</th><th>Party</th><th>Category</th><th>Details</th><th class="r">Received</th><th class="r">Paid</th></tr></thead><tbody>${result.entries.map((e) => `<tr><td>${esc(fmtDate(e.date))}</td><td>${esc(e.type)}</td><td>${esc(e.party)}</td><td>${esc(label(e.category))}</td><td>${esc(e.description)}</td><td class="r">${Number(e.in) ? money(e.in) : ''}</td><td class="r">${Number(e.out) ? money(e.out) : ''}</td></tr>`).join('')}</tbody></table>` : ''}
      <div class="sig"><div>Accountant</div><div>Principal</div></div>`;
    printPage('Income and Expenditure Report', title, body);
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Generate a financial report</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select size="small" label="Report" value={type} onChange={(e) => setType(e.target.value)} sx={{ minWidth: 190 }}>
            <MenuItem value="daily">Daily</MenuItem><MenuItem value="period">Custom period</MenuItem><MenuItem value="yearly">Whole year</MenuItem>
          </TextField>
          {type === 'daily' && <TextField size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} value={date} onChange={(e) => setDate(e.target.value)} />}
          {type === 'period' && (
            <>
              <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
              <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
            </>
          )}
          {type === 'yearly' && <TextField size="small" type="number" label="Year" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} sx={{ width: 120 }} />}
          <Button variant="contained" startIcon={<ReportIcon />} onClick={run}>Generate</Button>
          {result && <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportExcel}>Export Excel</Button>}
          {result && <Button variant="outlined" startIcon={<PrintIcon />} onClick={print}>Print</Button>}
        </Box>
      </Paper>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>}
      {!loading && result && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>{title}</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}><MiniStat label="Total income" value={money(result.totalIncome)} color="#00796b" bg="#e8f5e9" /></Grid>
            <Grid item xs={12} sm={4}><MiniStat label="Total outflow" value={money(result.totalExpense)} color="#ef6c00" bg="#fff3e0" /></Grid>
            <Grid item xs={12} sm={4}><MiniStat label="Net" value={money(result.netBalance)} color={Number(result.netBalance) >= 0 ? '#00796b' : '#d32f2f'} bg={Number(result.netBalance) >= 0 ? '#e0f2f1' : '#ffebee'} /></Grid>
          </Grid>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}><CategoryTable title="Income by category" rows={result.incomeByCategory} color="#00796b" /></Grid>
            <Grid item xs={12} md={6}><CategoryTable title="Outflow by category" rows={result.outflowByCategory} color="#ef6c00" /></Grid>
          </Grid>

          {result.months && (
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead><TableRow><TableCell>Month</TableCell><TableCell align="right">Income</TableCell><TableCell align="right">Outflow</TableCell><TableCell align="right">Net</TableCell></TableRow></TableHead>
                <TableBody>
                  {result.months.map((m) => (
                    <TableRow key={m.month} hover>
                      <TableCell>{m.month}</TableCell>
                      <TableCell align="right" sx={{ color: '#00796b' }}>{money(m.income)}</TableCell>
                      <TableCell align="right" sx={{ color: '#ef6c00' }}>{money(m.outflow)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: Number(m.net) >= 0 ? '#00796b' : '#d32f2f' }}>{money(m.net)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
          {result.entries && (
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <TableContainer sx={{ maxHeight: 460 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Type</TableCell><TableCell>Party</TableCell><TableCell>Category</TableCell><TableCell>Details</TableCell><TableCell align="right">Received</TableCell><TableCell align="right">Paid</TableCell></TableRow></TableHead>
                  <TableBody>
                    {result.entries.map((e, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{fmtDate(e.date)}</TableCell><TableCell>{e.type}</TableCell><TableCell>{e.party || '-'}</TableCell><TableCell>{label(e.category)}</TableCell><TableCell>{e.description || '-'}</TableCell>
                        <TableCell align="right" sx={{ color: '#00796b' }}>{Number(e.in) ? money(e.in) : ''}</TableCell>
                        <TableCell align="right" sx={{ color: '#ef6c00' }}>{Number(e.out) ? money(e.out) : ''}</TableCell>
                      </TableRow>
                    ))}
                    {result.entries.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No entries in this period</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
}
